import React from "react";
import { createListTableColumns, Grid, ListTable, Stack, Text } from "sprint-common-web";

import { LanguageDetailsInfo } from "../../../../common/components/details-info/LanguageDetailsInfo";
import { VideoDetailsInfo } from "../../../../common/components/details-info/VideoDetailsInfo";
import { DynamicSubtitleType } from "../../../../common/types/DynamicSubtitleType";
import { RecordType } from "../../../../common/types/RecordType";
import { renderRecordType } from "../../../../common/utils/renderRecordType";
import {
    mapToContentTitle,
    mapToContentVersions
} from "../../../../content-ingestion/common/asset-operations/AssetOperationListItem";
import { BulkEditCloudAssetContentItem } from "../../cloud-asset/sections/info/content/edit/bulk/BulkEditCloudAssetContentItem";

export type BulkAssetContentOperationListItem = BulkEditCloudAssetContentItem & {
    targetContentTitle: string;
    targetVersions: string[];
};

interface BulkAssetContentOperationListProps {
    operations: BulkAssetContentOperationListItem[];
    isLoading: boolean;
}

export const BulkAssetContentOperationList = ({
    operations,
    isLoading
}: BulkAssetContentOperationListProps): JSX.Element => {
    return (
        <Stack tokens={{ padding: "0 0 20px 0" }}>
            <ListTable isLoading={isLoading} columns={columns} items={operations} layoutMode="fixedColumns" />
        </Stack>
    );
};

const renderUniquenessKey = (item?: BulkAssetContentOperationListItem): JSX.Element => {
    if (!item) return <Text variant="smallPlus">-</Text>;

    switch (item.recordType) {
        case RecordType.Video:
            return (
                <VideoDetailsInfo
                    key="video"
                    subtitle={item.uniquenessKey.subtitleLanguage}
                    aspectRatio={item.uniquenessKey.aspectRatio}
                />
            );
        case RecordType.Audio:
            return <LanguageDetailsInfo key="audio" language={item.uniquenessKey.soundtrackLanguage} />;
        case RecordType.Subtitle:
            return (
                <Grid>
                    <LanguageDetailsInfo
                        key="subtitle"
                        language={item.uniquenessKey.subtitleLanguage}
                        subtitleType={DynamicSubtitleType[item.uniquenessKey.subtitleType]}
                    />
                </Grid>
            );
        default:
            return <Text variant="smallPlus">-</Text>;
    }
};

const renderFilepath = (item?: BulkAssetContentOperationListItem): JSX.Element => {
    if (!item) return <Text variant="smallPlus">-</Text>;

    return <Text variant="smallPlus">{item.filepath}</Text>;
};

const renderContent = (contentTitle: string, versions: string[]): JSX.Element => (
    <Stack tokens={{ childrenGap: 5 }}>
        <Text variant="smallPlus" value={contentTitle} styles={{ root: { fontWeight: "bold" } }} />
        {versions.map((version, key) => (
            <Text key={`${key}-${version}`} variant="smallPlus" value={version} />
        ))}
    </Stack>
);

const renderCurrentContent = (item?: BulkAssetContentOperationListItem): JSX.Element => {
    if (!item) return <Text variant="smallPlus">-</Text>;

    return renderContent(mapToContentTitle(item.content), mapToContentVersions(item.content));
};

const renderTargetContent = (item?: BulkAssetContentOperationListItem): JSX.Element => {
    if (!item) return <Text variant="smallPlus">-</Text>;

    return renderContent(item.targetContentTitle, item.targetVersions);
};

const columns = createListTableColumns<BulkAssetContentOperationListItem>([
    {
        type: "basic",
        key: "id",
        name: "Details",
        minWidth: 120,
        maxWidth: 220,
        onRender: renderUniquenessKey
    },
    {
        type: "basic",
        key: "recordType",
        name: "Record type",
        minWidth: 100,
        onRender: renderRecordType
    },

    {
        type: "basic",
        key: "content",
        name: "Current content",
        minWidth: 180,
        maxWidth: 280,
        onRender: renderCurrentContent
    },
    {
        type: "basic",
        key: "targetVersions",
        name: "Target content",
        minWidth: 180,
        maxWidth: 280,
        onRender: renderTargetContent
    },
    {
        type: "basic",
        key: "filepath",
        name: "Filepath",
        minWidth: 180,
        maxWidth: 330,
        onRender: renderFilepath
    }
]);
